import { useCallback } from "react"
import { ModalPanel, ModalPanelProps, OutlinedButton, Title } from "dash-styled"
import { ReactComponent as EditIcon } from "icons/lib/edit.svg"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"
import { ReactComponent as CheckIcon } from "icons/lib/check_circle.svg"
import { ReactComponent as WarnIcon } from "icons/lib/warn.svg"

enum ModalType {
    Edit = "edit",
    Warn = "warn"
}

type ModalProps = {
    onClose: () => void
    onSubmit: () => void
    testId?: string
    type?: "edit" | "warn"
    title: string
    mode?: "modal" | "paper"
    children: JSX.Element | JSX.Element[]
    submit: {
        label: string
        disabled?: boolean
    }
}

export const SchemaFormModal = ({
    onClose,
    onSubmit,
    testId,
    type = ModalType.Edit,
    mode = "modal",
    title,
    children,
    submit: { label, disabled }
}: ModalProps) => {
    const isModal = mode === "modal"

    const TitleIcon = type === ModalType.Edit ? EditIcon : WarnIcon

    const Container = useCallback(
        ({ children, className, testId, ...props }: ModalPanelProps) =>
            isModal ? (
                <ModalPanel
                    testId={testId}
                    children={children}
                    className={className}
                    {...props}
                />
            ) : (
                <div
                    className={className}
                    children={children}
                    data-testid={testId}
                />
            ),
        [isModal]
    )

    return (
        <Container
            onClose={onClose}
            hideClose
            noPadding
            testId={testId}
            className={isModal ? `max-w-lg w-full` : "w-full h-full"}
        >
            <div className="flex items-center justify-between px-4 py-3 bg-white rounded-t-xl">
                <div className="flex items-center">
                    <TitleIcon className="text-rhino-970 w-4 mr-3" />
                    <Title
                        component="h2"
                        variant="sm"
                        weight="semibold"
                        noMargin
                        className="uppercase tracking-widest text-rhino-970"
                    >
                        {title}
                    </Title>
                </div>
                <button
                    data-testid="close-button"
                    onClick={onClose}
                    className="right-0 top-0 p-1 ml-2"
                >
                    <IconClose className="w-4 h-4" />
                </button>
            </div>
            <div
                className={`rounded-b-xl p-4 bg-white shadow-inner ${
                    !isModal
                        ? "flex flex-col justify-between overflow-auto"
                        : ""
                }`}
                style={
                    !isModal
                        ? {
                              height: "calc(100% - 48px)"
                          }
                        : {}
                }
            >
                <div>{children}</div>
                <div className="flex justify-end mt-10">
                    <OutlinedButton
                        testId="form-cancel-button"
                        label="Cancel"
                        marginLeft="2"
                        className="bg-smoke-20"
                        onClick={onClose}
                    />
                    <OutlinedButton
                        testId="form-submit-button"
                        disabled={disabled}
                        Icon={type === ModalType.Edit ? CheckIcon : undefined}
                        label={label}
                        marginLeft="2"
                        className={`text-white ${
                            type === ModalType.Edit
                                ? "bg-grape-500"
                                : "bg-blood-600 py-2.5"
                        } ${disabled ? "opacity-60" : ""}`}
                        onClick={onSubmit}
                    />
                </div>
            </div>
        </Container>
    )
}
